/**
 * legend.js — vertical colour-strip legend (port of bundle drawLegend / addLegendSquares).
 *
 * Export:
 *   drawLegend(legendSel, bandCfg, chartHeight, prettyUnit)
 *
 * Signature matches what renderer.js will call:
 *   legendSel   — d3 selection of the legend <g> element
 *   bandCfg     — { labels, thresholds } from bandConfig() / centeredBandConfig()
 *   chartHeight — inner chart pixel height (used to compute swatch height)
 *   prettyUnit  — display string for the unit label (e.g. 'm/s', '°C', 'kt')
 *
 * Layout (bundle line 1960-2013):
 *   - Clears legendSel children.
 *   - Moves legendSel to translate(marginLeft-1, marginTop+20) — caller is expected
 *     to have already positioned the <g>; this function only sets relative offsets
 *     inside it (we do NOT re-translate here; the renderer places the <g> itself).
 *   - Draws the unit title text centred above the strip.
 *   - Draws one <rect>+<text> pair per label, top = highest value (labels reversed).
 */

const SWATCH_WIDTH = 25;   // bundle: r = 25
const TOP_PADDING = 20;    // bundle: c = 20  (space for the title above the swatches)

/**
 * drawLegend — vertical colour-strip legend ported from bundle 1960-2013.
 *
 * @param {object} legendSel    d3 selection of a <g> element
 * @param {object} bandCfg      { labels: [{value, rgb}], thresholds: [...] }
 * @param {number} chartHeight  inner chart pixel height
 * @param {string} prettyUnit   unit label string
 */
export function drawLegend(legendSel, bandCfg, chartHeight, prettyUnit, titleColor = 'black') {
  legendSel.selectAll('*').remove();

  const { labels } = bandCfg;
  if (!labels || labels.length === 0) return;

  const availableHeight = chartHeight - TOP_PADDING; // bundle: f = this.height - c
  const swatchHeight = availableHeight / labels.length; // bundle: w = f / u

  // Title above the strip (bundle addLegendTitle)
  legendSel
    .append('text')
    .attr('y', -TOP_PADDING / 2)
    .attr('x', SWATCH_WIDTH / 2)
    .style('text-anchor', 'middle')
    .style('font-size', '13px')
    .style('font-weight', 'bold')
    .style('fill', titleColor)
    .text(prettyUnit ?? '');

  // Swatches drawn top-to-bottom = highest value first (labels.slice().reverse())
  // (bundle addLegendSquares: n.slice().reverse().forEach((r, c) => ...))
  labels
    .slice()
    .reverse()
    .forEach((band, idx) => {
      // Colour swatch
      legendSel
        .append('rect')
        .attr('width', SWATCH_WIDTH)
        .attr('height', swatchHeight)
        .attr('y', idx * swatchHeight)
        .attr('fill', `rgb(${band.rgb.join(',')})`);

      // Value text centred in the swatch
      legendSel
        .append('text')
        .attr('y', idx * swatchHeight + swatchHeight / 2)
        .attr('x', SWATCH_WIDTH / 2)
        .attr('dy', '0.35em')
        .style('text-anchor', 'middle')
        .style('font-size', '11px')
        .style('fill', 'white')
        .style('stroke', 'black')
        .style('stroke-width', '0.5px')
        .style('paint-order', 'stroke')
        .style('font-weight', 'bold')
        .text(band.value);
    });
}
